import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users, Video, FolderOpen, Clock, CheckCircle, AlertCircle, Upload, Mic, Languages, Send, CheckCircle2, XCircle } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { getUsers, getSessions, getProjects } from '../services/api';
import { getRecordingHours } from '../services/annotationApi';
import { ROLE_LABELS, roleColorClasses } from '../utils/roleStyles';
import { format, subDays } from 'date-fns';
import { formatHm } from '../utils/formatDuration';

type DateMode = 'all' | '24h' | '7d' | '30d' | 'custom';

function presetRange(mode: DateMode): { startDate?: string; endDate?: string } {
  const now = Date.now();
  switch (mode) {
    case '24h': return { startDate: new Date(now - 24 * 3600 * 1000).toISOString() };
    case '7d': return { startDate: new Date(now - 7 * 24 * 3600 * 1000).toISOString() };
    case '30d': return { startDate: new Date(now - 30 * 24 * 3600 * 1000).toISOString() };
    default: return {};
  }
}

const COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'];
const LANGUAGE_COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#14b8a6', '#a855f7', '#0ea5e9', '#84cc16'];

function StatCard({
  label, value, icon: Icon, color, sub,
}: {
  label: string;
  value: number | string;
  icon: React.ElementType;
  color: string;
  sub?: string;
}) {
  return (
    <div className="card flex items-start gap-4">
      <div className={`p-3 rounded-xl ${color}`}>
        <Icon size={22} className="text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-sm text-gray-500 mt-0.5">{label}</p>
        {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  // Date range for the recording-hour cards (Submitted windows on submission time).
  const [dateMode, setDateMode] = useState<DateMode>('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const dateRange = useMemo(() => {
    if (dateMode === 'custom') {
      return {
        startDate: customStart ? new Date(customStart).toISOString() : undefined,
        endDate: customEnd ? new Date(customEnd + 'T23:59:59').toISOString() : undefined,
      };
    }
    return presetRange(dateMode);
  }, [dateMode, customStart, customEnd]);

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    // Bump past the backend's default-50 cap so the Total Users tile +
    // role-breakdown reflect actual platform headcount, not just the first
    // GSI page.
    queryFn: () => getUsers({ limit: '5000' }),
  });

  const { data: pendingData } = useQuery({
    queryKey: ['users-pending'],
    queryFn: () => getUsers({ approvalStatus: 'pending', limit: '5000' }),
  });

  const { data: sessionsData } = useQuery({
    queryKey: ['sessions'],
    queryFn: () => getSessions({ limit: '5000' }),
  });

  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => getProjects({ status: 'active' }),
  });

  // Recording-hour buckets for the caller's scope — computed by the shared
  // backend function (platform for admin, assigned supervisors for sub-admin,
  // own team for supervisor). Same numbers as the Supervisor Analytics table;
  // no 5000-row client fetch or client-side bucketing.
  const { data: hours } = useQuery({
    queryKey: ['recording-hours', dateRange.startDate ?? '', dateRange.endDate ?? ''],
    queryFn: () => getRecordingHours(dateRange),
  });

  const users = usersData?.users ?? [];
  const pending = pendingData?.users ?? [];
  const sessions = sessionsData?.sessions ?? [];
  const projects = projectsData?.projects ?? [];

  // Sessions by day (last 14 days)
  const sessionsByDay = Array.from({ length: 14 }, (_, i) => {
    const date = subDays(new Date(), 13 - i);
    const dateStr = format(date, 'yyyy-MM-dd');
    const count = sessions.filter(
      (s: { createdAt: string }) => s.createdAt.startsWith(dateStr),
    ).length;
    return { date: format(date, 'MMM dd'), count };
  });

  // Sessions by status
  const statusCounts = sessions.reduce(
    (acc: Record<string, number>, s: { status: string }) => {
      acc[s.status] = (acc[s.status] ?? 0) + 1;
      return acc;
    },
    {},
  );
  const statusData = Object.entries(statusCounts).map(([name, value]) => ({ name, value }));

  // Upload health
  const completed = sessions.filter((s: { status: string }) => s.status === 'completed').length;
  const failed = sessions.filter((s: { status: string }) => s.status === 'failed').length;
  const uploading = sessions.filter((s: { status: string }) => s.status === 'uploading').length;

  // Recording-hour buckets straight from the backend (shared function → the
  // same numbers as Supervisor Analytics, for every role).
  const totalDurationSeconds = hours?.recordingSeconds ?? 0;
  const submittedDurationSeconds = hours?.submittedSeconds ?? 0;
  const approvedDurationSeconds = hours?.supervisorApprovedSeconds ?? 0;
  const rejectedDurationSeconds = hours?.rejectedSeconds ?? 0;

  // Aggregate duration & session count per primary language
  const byLanguage: Record<string, { sessions: number; seconds: number }> = {};
  sessions.forEach((s: { language?: string; duration?: number; status?: string }) => {
    // Only completed sessions are real uploads — exclude incomplete drafts.
    if (s.status !== 'completed') return;
    const lang = (s.language && s.language.trim()) || 'Unknown';
    byLanguage[lang] ??= { sessions: 0, seconds: 0 };
    byLanguage[lang].sessions += 1;
    byLanguage[lang].seconds += s.duration ?? 0;
  });
  const languageRows = Object.entries(byLanguage)
    .map(([name, v]) => ({ name, sessions: v.sessions, seconds: v.seconds }))
    .sort((a, b) => b.seconds - a.seconds);
  const languagePieData = languageRows.filter((r) => r.seconds > 0);

  // Approved-user counts per role — drives the role-breakdown tile row.
  // Disabled / pending / rejected users are excluded so the tiles reflect
  // active platform headcount.
  const approvedUsers = users.filter((u: { approvalStatus?: string }) => u.approvalStatus === 'approved');
  const usersByRole: Record<string, number> = {
    enumerator: 0,
    transcriber: 0,
    supervisor: 0,
    admin: 0,
  };
  approvedUsers.forEach((u: { role?: string }) => {
    if (u.role && Object.prototype.hasOwnProperty.call(usersByRole, u.role)) {
      usersByRole[u.role]++;
    }
  });
  const ROLE_ORDER: Array<keyof typeof usersByRole> = ['enumerator', 'transcriber', 'supervisor', 'admin'];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500 text-sm mt-1">Platform overview — DataOne Field Data Collection</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Users"
          value={users.length}
          icon={Users}
          color="bg-brand-500"
          sub={`${pending.length} pending approval`}
        />
        <StatCard
          label="Active Projects"
          value={projects.length}
          icon={FolderOpen}
          color="bg-accent-500"
        />
        <StatCard
          label="Videos Uploaded"
          value={completed}
          icon={Video}
          color="bg-emerald-500"
          sub={`${sessions.length} total sessions`}
        />
        <StatCard
          label="Pending Approvals"
          value={pending.length}
          icon={Clock}
          color="bg-amber-500"
        />
      </div>

      {/* Recording-hour section: date filter + buckets. Submitted/Approved/
          Rejected window on submission time; supervisors see their own team,
          admins see the platform. */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-gray-500 mr-1">Recording hours</span>
          {(['24h', '7d', '30d', 'all'] as DateMode[]).map((m) => (
            <button
              key={m}
              onClick={() => setDateMode(m)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                dateMode === m ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {m === 'all' ? 'All Time' : `Last ${m}`}
            </button>
          ))}
          <button
            onClick={() => setDateMode('custom')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium ${
              dateMode === 'custom' ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Custom
          </button>
          {dateMode === 'custom' && (
            <>
              <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="input max-w-[10rem]" />
              <span className="text-gray-400">→</span>
              <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="input max-w-[10rem]" />
            </>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="Total Recording"
            value={formatHm(totalDurationSeconds)}
            icon={Mic}
            color="bg-violet-500"
          />
          <StatCard
            label="Transcribed & Submitted"
            value={formatHm(submittedDurationSeconds)}
            icon={Send}
            color="bg-amber-500"
          />
          <StatCard
            label="Approved Recording"
            value={formatHm(approvedDurationSeconds)}
            icon={CheckCircle2}
            color="bg-emerald-500"
          />
          <StatCard
            label="Rejected Recording"
            value={formatHm(rejectedDurationSeconds)}
            icon={XCircle}
            color="bg-red-500"
          />
        </div>
      </div>

      {/* Users by role — approved-only counts, colour-coded per the
          platform palette so admins can scan headcount at a glance. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {ROLE_ORDER.map((role) => (
          <div
            key={role}
            className={`flex items-center justify-between rounded-xl px-4 py-3 ${roleColorClasses(role)}`}
          >
            <span className="text-sm font-medium">{ROLE_LABELS[role]}s</span>
            <span className="text-2xl font-bold">{usersByRole[role]}</span>
          </div>
        ))}
      </div>

      {/* Pending Approval Alert */}
      {pending.length > 0 && (
        <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <AlertCircle size={20} className="text-amber-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-800">
              {pending.length} user{pending.length > 1 ? 's' : ''} awaiting approval
            </p>
            <p className="text-xs text-amber-600 mt-0.5">
              Go to Users → Pending Approvals to review
            </p>
          </div>
          <a href="/users?tab=pending" className="text-sm font-medium text-amber-700 hover:text-amber-900">
            Review →
          </a>
        </div>
      )}

      {/* Upload Health */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card text-center">
          <CheckCircle size={28} className="text-emerald-500 mx-auto mb-2" />
          <p className="text-2xl font-bold text-gray-900">{completed}</p>
          <p className="text-sm text-gray-500">Completed</p>
        </div>
        <div className="card text-center">
          <Upload size={28} className="text-blue-500 mx-auto mb-2" />
          <p className="text-2xl font-bold text-gray-900">{uploading}</p>
          <p className="text-sm text-gray-500">Uploading</p>
        </div>
        <div className="card text-center">
          <AlertCircle size={28} className="text-red-500 mx-auto mb-2" />
          <p className="text-2xl font-bold text-gray-900">{failed}</p>
          <p className="text-sm text-gray-500">Failed</p>
        </div>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sessions over time */}
        <div className="card lg:col-span-2">
          <h2 className="text-base font-semibold text-gray-800 mb-4">Sessions (Last 14 Days)</h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={sessionsByDay} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Status pie */}
        <div className="card">
          <h2 className="text-base font-semibold text-gray-800 mb-4">Session Status</h2>
          {statusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="45%"
                  innerRadius={55}
                  outerRadius={85}
                  dataKey="value"
                  paddingAngle={3}
                >
                  {statusData.map((_, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Legend
                  formatter={(value) => (
                    <span className="text-xs text-gray-600 capitalize">{value}</span>
                  )}
                />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">
              No session data yet
            </div>
          )}
        </div>
      </div>

      {/* Language breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card lg:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <Languages size={18} className="text-violet-600" />
            <h2 className="text-base font-semibold text-gray-800">Recording by Language</h2>
          </div>
          {languageRows.length === 0 ? (
            <div className="h-32 flex items-center justify-center text-gray-400 text-sm">
              No language data yet
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Language</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Sessions</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Minutes</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Hours</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Duration</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {languageRows.map((row, idx) => (
                    <tr key={row.name} className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span
                            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: LANGUAGE_COLORS[idx % LANGUAGE_COLORS.length] }}
                          />
                          <span className="font-medium text-gray-800">{row.name}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right text-gray-700">{row.sessions}</td>
                      <td className="px-3 py-2 text-right text-gray-700">{Math.round(row.seconds / 60).toLocaleString()}</td>
                      <td className="px-3 py-2 text-right text-gray-700">{(row.seconds / 3600).toFixed(2)}</td>
                      <td className="px-3 py-2 text-right text-gray-500">{formatHm(row.seconds)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card">
          <h2 className="text-base font-semibold text-gray-800 mb-4">Language Share (by duration)</h2>
          {languagePieData.length === 0 ? (
            <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">
              No duration data yet
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={languagePieData}
                  cx="50%"
                  cy="45%"
                  innerRadius={55}
                  outerRadius={85}
                  dataKey="seconds"
                  nameKey="name"
                  paddingAngle={3}
                >
                  {languagePieData.map((_, index) => (
                    <Cell key={index} fill={LANGUAGE_COLORS[index % LANGUAGE_COLORS.length]} />
                  ))}
                </Pie>
                <Legend
                  formatter={(value) => (
                    <span className="text-xs text-gray-600">{value}</span>
                  )}
                />
                <Tooltip
                  formatter={(value: number) => [formatHm(value), 'Recording']}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
